/**
 * useQueryHooks.js
 *
 * Centralized React Query hooks for the most-fetched API endpoints.
 * Benefits over raw useEffect fetching:
 *  - Global cache: data persists across page navigation
 *  - Instant loads: cached data renders immediately on re-visit
 *  - Background refetch: silently updates stale data without a loading spinner
 *  - Deduplication: parallel components requesting the same data share one request
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getBatches,
  getStaff,
  getStaffByIds,
  getAllSubscribedUsers,
  getServices,
  getSports,
  getServiceWithBatches,
} from 'services/vendorAPI';

// ─── Query Keys ──────────────────────────────────────────────────────────────
// Export these so WebSocket handlers can invalidate specific caches
export const QUERY_KEYS = {
  batches: ['batches'],
  staff: ['staff'],
  subscribedUsers: ['subscribedUsers'],
  services: ['services'],
  sports: ['sports'],
};

// ─── Batches ─────────────────────────────────────────────────────────────────
/**
 * Fetches all vendor batches.
 * staleTime: 5 minutes — data is re-used across components for 5 mins before a silent refetch.
 */
export const useBatchesQuery = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.batches,
    queryFn: async () => {
      const response = await getBatches();
      if (!response.success) throw new Error(response.error || 'Failed to load batches');
      return response.data?.batches || response.data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// ─── Batches by Service ───────────────────────────────────────────────────────
/**
 * Fetches batches for a SPECIFIC service using getServiceWithBatches.
 * Keyed by serviceId so each service tab gets its own cache slot.
 * staleTime: 2 minutes — invalidated immediately after adding/editing a batch.
 */
export const useServiceBatchesQuery = (serviceId, options = {}) => {
  return useQuery({
    queryKey: ['batches', 'byService', serviceId],
    queryFn: async () => {
      if (!serviceId) return [];
      const response = await getServiceWithBatches(serviceId);
      if (!response.success) throw new Error(response.error || 'Failed to load batches');
      // Batches are nested inside the service object
      if (response.data?.batches && Array.isArray(response.data.batches)) {
        return response.data.batches;
      }
      if (Array.isArray(response.data)) {
        const service = response.data.find(s => s.id === parseInt(serviceId));
        return service?.batches || [];
      }
      return [];
    },
    enabled: !!serviceId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    ...options,
  });
};

// ─── Staff / Coaches ──────────────────────────────────────────────────────────
/**
 * Fetches all vendor staff.
 * staleTime: 5 minutes — staff lists change infrequently.
 */
export const useStaffQuery = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.staff,
    queryFn: async () => {
      const response = await getStaff();
      if (!response.success) throw new Error(response.error || 'Failed to load staff');
      return response.data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

/**
 * Fetches staff by an array of IDs.
 * Caches by the sorted ID list so same coach assignments across batches share cache.
 * staleTime: 5 minutes
 */
export const useStaffByIdsQuery = (coachIds = [], options = {}) => {
  const normalizedIds = Array.isArray(coachIds)
    ? coachIds.filter(Boolean).map(id => Number(id)).sort((a, b) => a - b)
    : [];
  const queryKey = ['staff', 'byIds', normalizedIds.join(',')];

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (!normalizedIds.length) return [];
      const response = await getStaffByIds(normalizedIds);
      if (!response.success) throw new Error(response.error || 'Failed to load staff');
      const staffList = Array.isArray(response.data) ? response.data : (response.data?.staff || []);
      return staffList.filter(coach => coach !== null);
    },
    enabled: normalizedIds.length > 0 && (options.enabled ?? true),
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// ─── All Subscribed Users ─────────────────────────────────────────────────────
/**
 * Fetches all subscribed users (trainees) for the vendor.
 * This is the heaviest endpoint — caching it prevents repeated heavy loads
 * when switching between batches or navigating back to Batch Info modal.
 * staleTime: 2 minutes — subscriptions change more frequently.
 */
export const useSubscribedUsersQuery = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.subscribedUsers,
    queryFn: async () => {
      const response = await getAllSubscribedUsers();
      if (!response.success) throw new Error(response.error || 'Failed to load subscribers');
      return response.data || [];
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    ...options,
  });
};

// ─── Services ─────────────────────────────────────────────────────────────────
/**
 * Fetches all vendor services.
 * staleTime: 10 minutes — services rarely change.
 */
export const useServicesQuery = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.services,
    queryFn: async () => {
      const response = await getServices();
      if (!response.success) throw new Error(response.error || 'Failed to load services');
      return response.data?.services || response.data || [];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    ...options,
  });
};

// ─── Sports / Service Icons ───────────────────────────────────────────────────
/**
 * Fetches global service_icons catalog (service_icons[].id === sport_id).
 * staleTime: 1 hour — icons rarely change.
 */
export const useSportsQuery = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.sports,
    queryFn: async () => {
      const response = await getSports();
      if (!response.success) throw new Error(response.error || 'Failed to load sports');
      return response.data || [];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    ...options,
  });
};

// ─── Cache Invalidation Helper ────────────────────────────────────────────────
/**
 * Returns a helper object to imperatively invalidate specific query caches.
 * Used by the WebSocket event handler when the server pushes change events.
 *
 * Example usage:
 *   const { invalidateBatches } = useQueryInvalidators();
 *   // On WebSocket event 'batch_updated':
 *   invalidateBatches();
 */
export const useQueryInvalidators = () => {
  const queryClient = useQueryClient();

  return {
    invalidateBatches: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.batches }),
    invalidateServiceBatches: (serviceId) => queryClient.invalidateQueries({ queryKey: ['batches', 'byService', serviceId] }),
    invalidateAllServiceBatches: () => queryClient.invalidateQueries({ queryKey: ['batches', 'byService'] }),
    invalidateStaff: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.staff }),
    invalidateSubscribedUsers: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscribedUsers }),
    invalidateServices: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services }),
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.batches });
      queryClient.invalidateQueries({ queryKey: ['batches', 'byService'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.staff });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscribedUsers });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services });
    },
  };
};
